import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Package, ShoppingCart, Loader2, Download, Euro, Minus } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VendorAnalyticsPage = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async (windowDays) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [o, s, t] = await Promise.all([
        axios.get(`${API}/analytics/vendor/overview?days=${windowDays}`, { headers }),
        axios.get(`${API}/analytics/vendor/sales-timeseries?days=${windowDays}`, { headers }),
        axios.get(`${API}/analytics/vendor/top-products?days=${windowDays}&limit=5`, { headers }),
      ]);
      setOverview(o.data);
      setSeries(s.data.series || []);
      setTopProducts(t.data.rows || []);
    } catch (err) {
      toast.error(T('Erreur de chargement', 'Loading error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchAll(days); /* eslint-disable-next-line */ }, [days, token]);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/analytics/vendor/csv-export?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `vendor-orders-${days}d.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error(T('Échec de l\'export', 'Export failed')); }
  };

  const fmtMoney = (n) => new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' }).format(n || 0);

  return (
    <div className="container-main py-8" data-testid="vendor-analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#E9C46A] font-semibold">{T('Performance', 'Performance')}</p>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C5C] tracking-tight mt-1">
            {T('Tableau de bord analytics', 'Analytics dashboard')}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <TabsList className="bg-white border border-[#0F4C5C]/10">
              <TabsTrigger value="7" data-testid="period-7d">7j</TabsTrigger>
              <TabsTrigger value="30" data-testid="period-30d">30j</TabsTrigger>
              <TabsTrigger value="90" data-testid="period-90d">90j</TabsTrigger>
              <TabsTrigger value="365" data-testid="period-365d">1 an</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={exportCsv} variant="outline" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> {T('CSV', 'CSV')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C5C]" /></div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-8">
            <KpiCard
              title={T('Chiffre d\'affaires', 'Revenue')}
              value={fmtMoney(overview?.revenue)}
              growth={overview?.growth_pct}
              icon={Euro}
              accent="#0F4C5C"
              testid="kpi-revenue"
            />
            <KpiCard
              title={T('Commandes', 'Orders')}
              value={overview?.orders_count ?? 0}
              icon={ShoppingCart}
              accent="#2A9D8F"
              testid="kpi-orders"
            />
            <KpiCard
              title={T('Articles vendus', 'Items sold')}
              value={overview?.items_sold ?? 0}
              icon={Package}
              accent="#E9C46A"
              testid="kpi-items"
            />
            <KpiCard
              title={T('Panier moyen', 'Avg. order')}
              value={fmtMoney(overview?.aov)}
              icon={TrendingUp}
              accent="#0B3A47"
              testid="kpi-aov"
            />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 shadow-sm">
              <h2 className="font-bold text-[#0F4C5C] tracking-tight mb-4">{T('Revenus quotidiens', 'Daily revenue')}</h2>
              {series.every((s) => s.revenue === 0) ? (
                <EmptyState text={T('Pas encore de ventes sur cette période.', 'No sales yet on this period.')} />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={series} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                    <defs>
                      <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0F4C5C" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#0F4C5C" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#0F4C5C20" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#0F4C5C' }} tickFormatter={(d) => d?.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: '#0F4C5C' }} tickFormatter={(v) => `${v}€`} />
                    <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 12, border: '1px solid #0F4C5C20' }} />
                    <Area type="monotone" dataKey="revenue" stroke="#0F4C5C" strokeWidth={2.5} fill="url(#rev-grad)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 shadow-sm">
              <h2 className="font-bold text-[#0F4C5C] tracking-tight mb-4">{T('Commandes', 'Orders')}</h2>
              {series.every((s) => s.orders === 0) ? (
                <EmptyState text={T('Aucune commande.', 'No orders.')} />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={series} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#0F4C5C20" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#0F4C5C' }} tickFormatter={(d) => d?.slice(8)} />
                    <YAxis tick={{ fontSize: 11, fill: '#0F4C5C' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #0F4C5C20' }} />
                    <Bar dataKey="orders" fill="#E9C46A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Top products */}
          <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 shadow-sm">
            <h2 className="font-bold text-[#0F4C5C] tracking-tight mb-4">{T('Top 5 produits', 'Top 5 products')}</h2>
            {topProducts.length === 0 ? (
              <EmptyState text={T('Pas de classement disponible — pas encore de ventes.', 'No ranking available — no sales yet.')} />
            ) : (
              <div className="space-y-2.5" data-testid="top-products-list">
                {topProducts.map((p, idx) => (
                  <div key={p.product_id} className="flex items-center gap-4 py-2.5 border-b border-[#0F4C5C]/5 last:border-0">
                    <span className="text-xl font-bold text-[#0F4C5C]/30 w-6">{idx + 1}</span>
                    {p.image && <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#0F4C5C] truncate">{p.name}</p>
                      <p className="text-xs text-[#0F4C5C]/60">{p.qty} {T('vendus', 'sold')}</p>
                    </div>
                    <p className="font-bold text-[#0F4C5C]">{fmtMoney(p.revenue)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const KpiCard = ({ title, value, growth, icon: Icon, accent, testid }) => (
  <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      {growth != null && (
        <span className={`text-xs font-bold flex items-center gap-1 px-2 py-0.5 rounded-full ${growth >= 0 ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
          {growth >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {growth >= 0 ? '+' : ''}{growth}%
        </span>
      )}
    </div>
    <p className="text-xs text-[#0F4C5C]/60 uppercase tracking-wider">{title}</p>
    <p className="text-2xl font-bold text-[#0F4C5C] tracking-tight mt-1">{value}</p>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <Minus className="w-8 h-8 text-[#0F4C5C]/30 mb-2" />
    <p className="text-sm text-[#0F4C5C]/60">{text}</p>
  </div>
);
